import {createStore, filterNil} from '@ngneat/elf';
import {deleteEntities, selectEntity, selectMany, updateEntities, upsertEntities, withEntities} from "@ngneat/elf-entities";
import {Injectable} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AgendaelementModell, ModelloppdateringType, MøteId, MøteMedAggregaterModell, MøteModellEnkel, MøteRessursClient, OmkringliggendeMøterModell, OppgaveModell, SaksdokumentModell} from "../ressurser/generertNg2HttpKlient";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {MonoTypeOperatorFunction, Observable} from "rxjs";
import {tap} from "rxjs/operators";
import {sorterLevertSamling, utelatFørsteDersomNil} from "felles/ng2/rxjsHjelpere";
import {ZonedDateTimePeriodeLukket} from "felles-ng2/typer/ZonedDateTimePeriode";
import {sorterMøterPåDato} from "felles-ng2/modell/møteModellHjelpere";
import {skrivZonedDateTimeSerialisert} from "../../støttefunksjoner/temporal";
import {Temporal} from "temporal-polyfill";
import {distinctUntilChangedDeep} from "felles-ng2/støttefunksjoner/observable";
import {ModellOpprettetEllerOppdatertHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellOpprettetEllerOppdatertHendelseHåndteringBygger";
import {ModellSlettetHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellSlettetHendelseHåndteringBygger";
import {sakerDerBrukerErAnsvarlig} from "felles-ng2/modell/sakModellHjelpere";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {erOppgaveForfalt} from "felles-ng2/modell/oppgaveModellHjelpere";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {antallDokumenterLest} from "felles-ng2/modell/dokumentModellHjelpere";
import {FeilhåndteringArgumenter, Feilhåndteringsstrategi, FeilhåndteringsstrategiForDataOgMeta, Leveringsvalg, serverFraLagerOgRequestPakketIMeta, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {DataOgMeta, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {HttpResponse} from "@angular/common/http";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {Modelloppdateringer, OppdateringmodellMøteOppdatert, OppdateringmodellMøteSlettet} from "felles-ng2/tjenester/Modelloppdateringer";
import ZonedDateTime = Temporal.ZonedDateTime;
import {AgendaelementModellDLM} from "felles-ng2/tjenester/datalager/SakDatatjeneste";

const debug = initDebugger('MøteMedAggregaterDatatjeneste');

/** Datalager for {@link MøteMedAggregaterModell} */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class MøteMedAggregaterDatatjeneste {

    /** Lager for {@link MøteMedAggregaterModell}.*/
      //public for testbarhet
    readonly møteLager = createStore({name: 'møteMedAggregater'}, withEntities<MøteMedAggregaterModell>());

    /** Holder rede på åpne subscriptions basert på møteId. Benyttes ifbm modelloppdateringer. */
      //public for testbarhet
    readonly møteAbonnenter = new DatalagerAbonnenter('møteMedAggregater.id');

    private sorterPåDato = (a: MøteMedAggregaterModell, b: MøteMedAggregaterModell) => sorterMøterPåDato(a.moete, b.moete);

    constructor(private møteRessurs: MøteRessursClient,
                private modelloppdatering: Modelloppdateringer,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private kontekst: BrukerkontekstTilbyder,
                private klokke: Klokke) {
        this.lyttTilModelloppdateringer();
    }

    oppdaterMøteIILokallager(oppdatert: MøteModellEnkel) {
        this.møteLager.update(updateEntities(oppdatert.id,
          eksisterende => ({...eksisterende, moete: oppdatert} as MøteMedAggregaterModell)));
    }

    oppdaterAntallSakerIILokallager(møteId: MøteId, saker: AgendaelementModellDLM[]) {
        this.møteLager.update(updateEntities(møteId,
          eksisterende =>
            ({
                ...eksisterende, agregater: {
                    ...eksisterende.agregater,
                    antallSaker: saker.length,
                    antallSakerMine: sakerDerBrukerErAnsvarlig(saker, this.kontekst.autentisertBruker()).length
                }
            } as MøteMedAggregaterModell)
        ));
    }

    oppdaterAntallOppgaverIILokallager(møteId: MøteId, oppgaver: OppgaveModell[]) {
        this.møteLager.update(updateEntities(møteId,
          eksisterende =>
            ({
                ...eksisterende, agregater: {
                    ...eksisterende.agregater,
                    antallOppgaver: oppgaver.length,
                    antallOppgaverForfalt: oppgaver.filter(oppgave => erOppgaveForfalt(oppgave, this.klokke)).length
                }
            } as MøteMedAggregaterModell)
        ));
    }

    oppdaterAntallDokumenterIILokallager(møteId: string, dokumenter: SaksdokumentModell[]) {
        const info = antallDokumenterLest(dokumenter);
        this.møteLager.update(updateEntities(møteId,
          eksisterende =>
            ({
                ...eksisterende, agregater: {
                    ...eksisterende.agregater,
                    antallDokumenter: info.antallTotalt,
                    antallUlesteDokumenter: info.antallUleste
                }
            } as MøteMedAggregaterModell)
        ));
    }

    slettMøteIILokallager(møteIder: MøteId[]) {
        this.møteLager.update(deleteEntities(møteIder));
    }

    /** Laster angitt møte.
     * Merk at dersom leveringsvalg = lever_undefined_ved_slettet vil returnert observable levere 'undefined' når forespurt entitet slettes i lageret */
    hentMedMeta(id: MøteId, leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<MøteMedAggregaterModell>>;
    hentMedMeta(id: MøteId, leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<MøteMedAggregaterModell | undefined>>;
    hentMedMeta(id: MøteId, leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<MøteMedAggregaterModell | undefined>> {
        return this.møteAbonnenter.observer(id,
          serverFraLagerOgRequestPakketIMeta({
              ressurskall: () => this.møteRessurs.hentMedAggregater(id),
              registrerILager: (respons: HttpResponse<MøteMedAggregaterModell>) => {
                  this.møteLager.update(upsertEntities(respons.body!));
                  return respons.body!
              },
              hentFraLager: () => this.møteLager.pipe(
                selectEntity(id),
                leveringsvalg == Leveringsvalg.ikke_lever_ved_slettet ? filterNil() : utelatFørsteDersomNil(),
                distinctUntilChangedDeep()), //kun lever verdier fra lageret ved reelle endringer
              ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente møte', 'hentMedMeta')
          }));
    }

    /** Laster angitt møte.
     * Merk at dersom leveringsvalg = lever_undefined_ved_slettet vil returnert observable levere 'undefined' når forespurt entitet slettes i lageret */
    hent(id: MøteId, leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<MøteMedAggregaterModell>
    hent(id: MøteId, leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<MøteMedAggregaterModell | undefined>
    hent(id: MøteId, leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<MøteMedAggregaterModell | undefined> {
        return this.hentMedMeta(id, leveringsvalg, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }
    førstkommendeMøteOgOmkringliggende(maksAntall: number): Observable<OmkringliggendeMøterModell> {
        return this.møteRessurs.finnFørsteMøteOgOmkringliggende({maksAntall}).somObservable()
          .pipe(this.registrerOmkringliggendeMøterILager());
    }

    møteOgOmkringliggende(møteId: MøteId, maksAntall: number): Observable<OmkringliggendeMøterModell> {
        return this.møteRessurs.finnMøteOgOmkringliggende2(møteId, {maksAntall}).somObservable()
          .pipe(this.registrerOmkringliggendeMøterILager());
    }

    finnTidligereMøter(før: ZonedDateTime, maksAntall: number): Observable<MøteMedAggregaterModell[]> {
        return this.møteRessurs.finnMøterMedStartFør({dato: skrivZonedDateTimeSerialisert(før), maksAntall}).somObservable()
          .pipe(this.registrerMøteMedAggregaterILager());
    }

    finnSenereMøter(etter: ZonedDateTime, maksAntall: number): Observable<MøteMedAggregaterModell[]> {
        return this.møteRessurs.finnMøterMedStartEtter({dato: skrivZonedDateTimeSerialisert(etter), maksAntall}).somObservable()
          .pipe(this.registrerMøteMedAggregaterILager());
    }

    finnMøterIPerioden(periode: ZonedDateTimePeriodeLukket): Observable<MøteMedAggregaterModell[]> {
        return this.møteRessurs.finnMøterIPerioden2(periode.somSerialiserbar()).somObservable()
          .pipe(this.registrerMøteMedAggregaterILager());
    }

    hentMangeFraFraLokallagerSomObservable(møteIder: MøteId[]): Observable<MøteMedAggregaterModell[]> {
        return this.møteAbonnenter.observerMange(møteIder,
          this.møteLager.pipe(
            selectMany(møteIder),
            sorterLevertSamling(this.sorterPåDato),
            //@todo hva inneholder array dersom møte blir slettet?
            distinctUntilChangedDeep())); //kun lever verdier fra lageret ved reelle endringer - viktig da vi ellers kan får rekursjoner i HorisontalMøtenavigasjonKomponent
    }


    //hjelpemetoder

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(
      feilhåndteringsstrategi: STRATEGI,
      brukerrettetFeilmelding: string,
      metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    private registrerOmkringliggendeMøterILager(): MonoTypeOperatorFunction<OmkringliggendeMøterModell> {
        return tap(møter => this.møteLager.update(upsertEntities(møter.moeter)));
    }

    private registrerMøteMedAggregaterILager(): MonoTypeOperatorFunction<MøteMedAggregaterModell[]> {
        return tap(møter => this.møteLager.update(upsertEntities(møter)));
    }

    /**
     * Lytt til relevante modelloppdateringer via {@link Modelloppdateringer} (push-baserte oppdateringer).
     * Merk at vi aldri avregistrerer subscription vi oppretter her da data-tjenesten aldri termineres.
     */
    private lyttTilModelloppdateringer() {
        //lytter ikke til ModelloppdateringType.NYTT_MØTE da vi ikke har noe abonnement basert på en 'forelder' (vi har kun abonnement på møte.id,
        // og vi vil aldri har noe abonnement på id til et nytt møte før møtet er opprettet...
        this.lyttTilMøteOppdatert();
        this.lyttTilMøteSlettet();
    }

    private lyttTilMøteOppdatert() {
        this.opprettetEllerOppdatertMøteHendelse<OppdateringmodellMøteOppdatert>(ModelloppdateringType.MØTE_OPPDATERT)
          .medAbonnentoppslag(this.møteAbonnenter, møteId => møteId)
          //.medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Møte "{møte}" oppdatert (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilMøteSlettet() {
        this.slettetHendelse<OppdateringmodellMøteSlettet>(ModelloppdateringType.MØTE_SLETTET)
          //.medFørLagerOppdatert(oppdatering => this.visMelding(i18n('Møte "{møte}" slettet (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell)))
          .oppdaterLagerVedHendelse();
    }


    private slettetHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellMedMøte>(hendelseType: ModelloppdateringType) {
        return new ModellSlettetHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, MøteMedAggregaterModell>(this.møteLager, this.modelloppdatering)
          .lyttTil(hendelseType)
          .medEntitetId(møteId => møteId);
    }

    private opprettetEllerOppdatertMøteHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellMedMøte>(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, MøteMedAggregaterModell>(this.møteLager, this.modelloppdatering)
          .lyttTil(hendelseType)
          .medEntitetId(møteId => møteId)
          .medEntitetLaster(møteId => this.møteRessurs.hentMedAggregater(møteId).råRespons());
    }
}

type OppdateringmodellMedMøte = MøteId;
